import { ConversationParticipant } from '@centecomm/portal-service';
import { useConversations, useCurrentUser } from '@centecomm/portal-service-react';
import format from 'date-fns/format';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import VisibilitySensor from 'react-visibility-sensor';
import { useDateFnsLocale } from 'Core/i18n/Hooks/useTranslationExtensions';
import { conversationsActions, conversationsSelectors } from 'State';
import { ConversationsActionTypes } from 'State/Conversations/actions';
import { MessageSentToConversation as MessageSentToConversationEvent } from 'Types/Conversations/Events';
import { MessageSentToConversationWrappedEvent, WrappedConversationEvent } from 'Types/Conversations/WrappedEvents';
import DynamicTranslatedContent from 'Core/i18n/Components/DynamicTranslatedContent';
import styles from './message-sent-to-conversation.module.css';

interface MessageSentToConversationProps {
    event: MessageSentToConversationEvent;
    conversationId: number;
}

function MessageSentToConversation({
    conversationId,
    event,
}: MessageSentToConversationProps): React.ReactElement<MessageSentToConversationProps> {
    const { t } = useTranslation(['users']);
    const dateFnsLocale = useDateFnsLocale();
    const dispatch = useDispatch();

    const { markLastMessageRead } = useConversations();
    const { currentUserId } = useCurrentUser();

    const { messageId, userId, message, metadata } = event;

    const { eventDate: date } = metadata;

    const currentParticipant: ConversationParticipant | undefined = useSelector(
        conversationsSelectors.conversationParticipant(conversationId, currentUserId as number),
    );

    const messageAuthor: ConversationParticipant | undefined = useSelector(
        conversationsSelectors.conversationParticipant(conversationId, userId),
    );

    const lastMessageReadForParticipant: number = useSelector(
        conversationsSelectors.lastMessageReadForParticipant(conversationId, currentUserId as number),
    );

    const messageEvents: WrappedConversationEvent[] = useSelector(
        conversationsSelectors.conversationMessageEvents(conversationId),
    );

    const [enableListener, toggleListener] = React.useState<boolean>(true);

    React.useEffect(() => {
        if (!!currentParticipant && messageId <= lastMessageReadForParticipant) {
            toggleListener(false);
        }
    }, [currentParticipant, lastMessageReadForParticipant]);

    const handleVisibility = async (isVisible: boolean) => {
        if (!isVisible || !currentParticipant || !messageEvents.length) return;

        const latestMessageId: number = messageEvents
            .map((wrappedEvent: MessageSentToConversationWrappedEvent) => wrappedEvent.event.messageId)
            .sort((a: number, b: number) => b - a)[0];

        if (messageId > lastMessageReadForParticipant && messageId === latestMessageId) {
            await markLastMessageRead(conversationId, messageId);
            dispatch({
                type: conversationsActions.CLEAR_UNREAD_MESSAGES,
                payload: conversationId,
            } as ConversationsActionTypes);

            dispatch({
                type: conversationsActions.UPDATE_LAST_MESSAGE_READ_FOR_PARTICIPANT,
                payload: {
                    conversationId,
                    participantId: currentUserId,
                    messageId: lastMessageReadForParticipant,
                },
            } as ConversationsActionTypes);

            toggleListener(false);
        }
    };

    return (
        <VisibilitySensor onChange={handleVisibility} active={enableListener}>
            <div className={`${styles.UserItem} ${userId === currentUserId ? styles.CurrentUser : styles.NotCurrentUser}`}>
                <div className={styles.MessageData}>
                    {messageAuthor && <p className={styles.MessageAuthor}>{messageAuthor.userName}</p>}
                    <p>{format(new Date(date), 'p', { locale: dateFnsLocale })}</p>
                </div>
                {messageAuthor && (
                    <p className={`${styles.MessageAuthorTitle}`}>
                        {messageAuthor.userType === 'tenant'
                            ? `${messageAuthor.occupant} @ ${messageAuthor.property}`
                            : t('users:userType.ownerOperator')}
                    </p>
                )}
                <div className={styles.MessageContent}>
                    <p>
                        <DynamicTranslatedContent
                            content={message}
                            showLoadingState
                            extendedOptions={{
                                bypassMainAppLanguage: true,
                            }}
                        >
                            {({ translatedContent }) => <>{translatedContent}</>}
                        </DynamicTranslatedContent>
                    </p>
                </div>
            </div>
        </VisibilitySensor>
    );
}

export default MessageSentToConversation;
